// src/pages/purchase-requisition/summary-pr/SummaryPR.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
    Typography, Box, Card, CardContent,
    CircularProgress, Alert, Snackbar, Stack,
    TextField, InputAdornment,
    ClickAwayListener, Tooltip, Button, Checkbox
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useOutletContext, useNavigate } from "react-router-dom";
import useAuthCheck from "../../../hooks/useAuthCheck";
import CustomBreadcrumbs from '../../../components/universal-features/breadcrumbs';
import * as XLSX from "xlsx";

// Import custom hooks
import useOrderData from "../hooks/useOrderData";
import useUserTooltipData from "../hooks/useUserTooltipData";
import useOrderSearch from "../hooks/useOrderSearch";

// Import components
import SummaryPRTable from "./components/SummaryPRTable";
import { formatDateTime } from "../../../utils/formatters"; // Import formatter

// New component for User Tooltip - moved here to be exported for OrderTable
export const UserTooltipDisplay = ({ uid, userName }) => {
    // Each instance of UserTooltipDisplay now uses its own useUserTooltipData hook
    const {
        tooltipOpen,
        tooltipContent,
        tooltipAnchorEl,
        handleTooltipOpen,
        handleTooltipClose
    } = useUserTooltipData();

    return (
        <ClickAwayListener onClickAway={handleTooltipClose}>
            <Tooltip
                open={tooltipOpen}
                onClose={handleTooltipClose}
                anchorEl={tooltipAnchorEl}
                title={
                    <Box sx={{ p: 1 }}>
                        <Typography variant="body2"><strong>Nama:</strong> {tooltipContent.name}</Typography>
                        <Typography variant="body2"><strong>Departemen:</strong> {tooltipContent.department}</Typography>
                        <Typography variant="body2"><strong>Jabatan:</strong> {tooltipContent.jabatan}</Typography>
                        <Typography variant="body2"><strong>Role:</strong> {tooltipContent.roles.join(', ')}</Typography>
                    </Box>
                }
            >
                <span
                    onMouseEnter={(e) => handleTooltipOpen(e, uid)}
                    onMouseLeave={handleTooltipClose}
                    style={{ cursor: 'pointer' }}
                >
                    {userName}
                </span>
            </Tooltip>
        </ClickAwayListener>
    );
};

// Helper function to get the final approval date
const getFinalApprovalDate = (order) => {
    if (!order.approval_history || !Array.isArray(order.approval_history)) {
        return '-';
    }
    
    // Find the last approval entry
    const finalApproval = order.approval_history
        .filter(entry => entry.decision === "Approved")
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
    
    return finalApproval ? formatDateTime(finalApproval.timestamp) : '-';
};

// Helper function to format tanggal creat for display (ditambahkan dari HistoryPengajuan)
const formatTanggalCreat = (tanggalCreat) => {
    if (!tanggalCreat) return '-';
    
    try {
        // Parsing tanggal menggunakan dayjs
        const date = new Date(tanggalCreat);
        
        if (isNaN(date.getTime())) {
            return tanggalCreat; // Return as-is jika parsing gagal
        }
        
        // Format: DD/MM/YYYY
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    } catch (error) {
        console.error("Error formatting tanggal creat:", error);
        return tanggalCreat;
    }
};

// Helper function to get tanggal creat info for tooltip (ditambahkan dari HistoryOrderTable)
const getTanggalCreatInfo = (order) => {
    if (!order.tanggal_creat) {
        return { date: '-', day: '-', time: '-', fullDate: null };
    }
    
    try {
        // Coba parsing tanggal_creat
        let tanggalCreatDate;
        
        // Jika tanggal_creat sudah dalam format Date object
        if (order.tanggal_creat instanceof Date) {
            tanggalCreatDate = order.tanggal_creat;
        } else {
            // Coba parsing dari string ISO (YYYY-MM-DDTHH:mm:ss) atau format lain
            tanggalCreatDate = new Date(order.tanggal_creat);
            
            // Jika parsing gagal, coba parsing dengan dayjs
            if (isNaN(tanggalCreatDate.getTime())) {
                const date = new Date(order.tanggal_creat);
                if (!isNaN(date.getTime())) {
                    tanggalCreatDate = date;
                } else {
                    // Coba format YYYY-MM-DD atau YYYY-MM-DDTHH:mm:ss
                    const dateStr = order.tanggal_creat;
                    if (dateStr.includes('T')) {
                        // ISO format dengan waktu
                        const [datePart, timePart] = dateStr.split('T');
                        if (datePart) {
                            const [year, month, day] = datePart.split('-').map(Number);
                            tanggalCreatDate = new Date(year, month - 1, day);
                        }
                    } else if (dateStr.includes('-')) {
                        // Format YYYY-MM-DD tanpa waktu
                        const [year, month, day] = dateStr.split('-').map(Number);
                        tanggalCreatDate = new Date(year, month - 1, day);
                    }
                }
            }
        }
        
        if (isNaN(tanggalCreatDate.getTime())) {
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
        
        // Format tanggal
        const day = tanggalCreatDate.getDate().toString().padStart(2, '0');
        const month = (tanggalCreatDate.getMonth() + 1).toString().padStart(2, '0');
        const year = tanggalCreatDate.getFullYear();
        
        // Format hari
        const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
        const dayName = days[tanggalCreatDate.getDay()];
        
        // Format waktu berdasarkan data yang tersimpan
        let timeString = '00:00:00';
        
        // Cek apakah ada informasi waktu dalam tanggal_creat
        if (order.tanggal_creat.includes('T')) {
            // Jika ada informasi waktu dalam ISO format
            const timePart = order.tanggal_creat.split('T')[1];
            if (timePart) {
                // Hapus bagian milidetik jika ada
                const timeWithoutMillis = timePart.split('.')[0];
                const timeParts = timeWithoutMillis.split(':');
                if (timeParts.length >= 2) {
                    const hours = timeParts[0].padStart(2, '0');
                    const minutes = timeParts[1].padStart(2, '0');
                    const seconds = timeParts[2] ? timeParts[2].padStart(2, '0') : '00';
                    timeString = `${hours}:${minutes}:${seconds}`;
                }
            }
        }
        
        return {
            date: `${day}/${month}/${year}`,
            day: dayName,
            time: timeString,
            fullDate: tanggalCreatDate
        };
    } catch (error) {
        console.error("Error parsing tanggal creat:", error);
        return { date: '-', day: '-', time: '-', fullDate: null };
    }
};

// Helper function to format date for display (seperti di OrderTable.jsx)
const formatDate = (date) => {
    if (!date) return '-';
    
    try {
        const d = new Date(date);
        
        if (isNaN(d.getTime())) {
            return date; // Return as-is jika parsing gagal
        }
        
        // Format: DD/MM/YYYY
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear();
        
        return `${day}/${month}/${year}`;
    } catch (error) {
        console.error("Error formatting date:", error);
        return date;
    }
};

// Helper function to get day name (seperti di OrderTable.jsx)
const getDayName = (date) => {
    if (!date) return '-';
    
    try {
        const d = new Date(date);
        
        if (isNaN(d.getTime())) {
            return '-';
        }
        
        const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
        return days[d.getDay()];
    } catch (error) {
        console.error("Error getting day name:", error);
        return '-';
    }
};

// Helper function to get time for display (seperti di OrderTable.jsx)
const getTime = (date) => {
    if (!date) return '-';
    
    try {
        const d = new Date(date);
        
        if (isNaN(d.getTime())) {
            return '-';
        }
        
        // Format: HH.mm.ss
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        const seconds = d.getSeconds().toString().padStart(2, '0');
        
        return `${hours}.${minutes}.${seconds}`;
    } catch (error) {
        console.error("Error getting time:", error);
        return '-';
    }
};

// Helper function to get submission info (seperti di OrderTable.jsx)
const getSubmissionInfo = (order) => {
    if (!order || !order.created_at) {
        return { date: '-', day: '-', time: '-', fullDate: null };
    }
    
    try {
        const submissionDate = new Date(order.created_at);
        
        return {
            date: formatDate(submissionDate),
            day: getDayName(submissionDate),
            time: getTime(submissionDate),
            fullDate: submissionDate
        };
    } catch (error) {
        console.error("Error formatting submission date:", error);
        return { date: '-', day: '-', time: '-', fullDate: null };
    }
};

function SummaryPR() {
    const { user, loading: authLoading, userProfile } = useAuthCheck();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [selectedOrders, setSelectedOrders] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // Use custom hook for order data
    const {
        orders,
        loadingOrders,
        loadingFlows,
        loadingAdminJabatanId,
        loadingUom,
        invManagementFreshPineOtherRoleId,
        developerRoleId,
        prcnInvMaterialPackPHRoleId,
        loadingRoles,
    } = useOrderData(user, userProfile);

    // Filter only approved orders that have PR number
    const approvedOrdersWithPR = useMemo(() => {
        return orders.filter(order => 
            order.status === "Approved" && 
            order.no_pr && // Check if no_pr exists
            typeof order.no_pr === 'string' && // Ensure it's a string
            order.no_pr.trim() !== '' && // Ensure it's not empty
            order.no_pr !== '-' // Ensure it's not just a dash
        );
    }, [orders]);

    // Use custom hook for order search
    const {
        searchTerm,
        setSearchTerm,
        filteredOrders,
    } = useOrderSearch(approvedOrdersWithPR); // Pass the approved orders with PR to the search hook

    // Function to handle individual checkbox selection
    const handleSelectOrder = (orderId) => {
        const newSelectedOrders = new Set(selectedOrders);
        if (newSelectedOrders.has(orderId)) {
            newSelectedOrders.delete(orderId);
        } else {
            newSelectedOrders.add(orderId);
        }
        setSelectedOrders(newSelectedOrders);
        setSelectAll(false);
    };

    // Function to handle select all checkbox
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedOrders(new Set());
        } else {
            const allOrderIds = new Set(filteredOrders.map(order => order.id));
            setSelectedOrders(allOrderIds);
        }
        setSelectAll(!selectAll);
    };

    // Function to download single order
    const handleDownloadExcel = (order) => {
        if (!order) {
            setSnackbar({ open: true, message: "Tidak ada data untuk diunduh.", severity: "warning" });
            return;
        }

        downloadOrdersToExcel([order]);
        setSnackbar({ open: true, message: "Data summary PR berhasil diunduh!", severity: "success" });
    };

    // Function to download multiple orders
    const handleDownloadSelected = () => {
        if (selectedOrders.size === 0) {
            setSnackbar({ open: true, message: "Pilih setidaknya satu order untuk diunduh.", severity: "warning" });
            return;
        }

        const selectedOrderData = filteredOrders.filter(order => selectedOrders.has(order.id));
        downloadOrdersToExcel(selectedOrderData);
        setSnackbar({ open: true, message: `${selectedOrders.size} data summary PR berhasil diunduh!`, severity: "success" });
    };

    // Generic function to download orders to Excel
    const downloadOrdersToExcel = (ordersToDownload) => {
        if (!ordersToDownload || ordersToDownload.length === 0) {
            setSnackbar({ open: true, message: "Tidak ada data untuk diunduh.", severity: "warning" });
            return;
        }

        const ws_data = [];
        const header = [
            "No",
            "Judul Order",
            "Tanggal Approve",
            "Diajukan Oleh",
            "No PR",
            "Tanggal Creat PR", // Ditambahkan kolom baru
            "Tanggal Pengajuan" // Ditambahkan kolom baru untuk pengajuan
        ];
        ws_data.push(header);

        // Add data rows
        ordersToDownload.forEach((order, index) => {
            const submissionInfo = getSubmissionInfo(order);
            const row = [
                index + 1,
                order.order_title || '-',
                getFinalApprovalDate(order),
                order.created_by_name || '-',
                order.no_pr || '-',
                formatTanggalCreat(order.tanggal_creat), // Ditambahkan data tanggal creat
                submissionInfo.date // Ditambahkan data tanggal pengajuan
            ];
            ws_data.push(row);
        });

        const ws = XLSX.utils.aoa_to_sheet(ws_data);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Summary PR");

        // Create file name
        const fileName = `Summary_PR_${ordersToDownload.length}_items_${new Date().toISOString().slice(0, 10)}.xlsx`;

        // Download file
        XLSX.writeFile(wb, fileName);
    };

    // Check if user has download permissions (Developer, Inv Management FreshPine & Other)
    const userRoles = userProfile?.id_role ? userProfile.id_role.split(';') : [];

    const canDownloadOrders = userRoles.includes(developerRoleId) || 
                             userRoles.includes(invManagementFreshPineOtherRoleId) ||
                         userRoles.includes(prcnInvMaterialPackPHRoleId);

  const allowedRolesForSummaryPR = [
    developerRoleId,
    invManagementFreshPineOtherRoleId,
    prcnInvMaterialPackPHRoleId 
  ];
  
  const canAccessSummaryPR = userRoles.some(roleId => 
    allowedRolesForSummaryPR.includes(roleId)
  );

  // Redirect to dashboard if user doesn't have access
  useEffect(() => {
    if (!authLoading && !loadingRoles && !canAccessSummaryPR) {
      navigate("/");
    }
  }, [authLoading, loadingRoles, canAccessSummaryPR, navigate]);

    if (authLoading || loadingOrders || loadingFlows || loadingAdminJabatanId || loadingUom || loadingRoles) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini.
                </Alert>
            </Box>
        );
    }

    // Show access denied if user doesn't have permission
  if (!canAccessSummaryPR) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Anda tidak memiliki akses ke halaman Summary PR.
        </Alert>
      </Box>
    );
  }

    return (
        <Box sx={{ p: 0 }}>
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Card>
                <CardContent sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h2">
                            Summary PR
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {/* Search Input */}
                            <TextField
                                variant="outlined"
                                size="small"
                                placeholder="Cari order..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: { xs: '100%', sm: 'auto' }, mr: 1 }}
                            />
                            
                            {/* Download Selected Button - Only show when items are selected */}
                            {selectedOrders.size > 0 && canDownloadOrders && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={handleDownloadSelected}
                                    size="small"
                                >
                                    Download ({selectedOrders.size})
                                </Button>
                            )}
                        </Stack>
                    </Stack>

                    <SummaryPRTable
                        orders={filteredOrders}
                        userProfile={userProfile}
                        invManagementFreshPineOtherRoleId={invManagementFreshPineOtherRoleId}
                        developerRoleId={developerRoleId}
                        handleDownloadExcel={handleDownloadExcel}
                        selectedOrders={selectedOrders}
                        selectAll={selectAll}
                        onSelectOrder={handleSelectOrder}
                        onSelectAll={handleSelectAll}
                        getTanggalCreatInfo={getTanggalCreatInfo} // Ditambahkan prop baru
                        formatTanggalCreat={formatTanggalCreat} // Ditambahkan prop baru
                        getSubmissionInfo={getSubmissionInfo} // Ditambahkan prop baru
                    />
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default SummaryPR;